import React, { useEffect, useState } from 'react';
import { useLandlordStore, Property } from '@/stores/useLandlordStore';
import { 
    ArrowLeft, 
    Users, 
    Plus, 
    Search, 
    Building, 
    User, 
    Mail, 
    Phone,
    MoreVertical,
    Loader2,
    CheckCircle,
    XCircle,
    Shield
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/contexts/ToastContext';

interface StaffMember {
    id: string;
    name: string;
    email: string;
    phone: string;
    role: string;
    status: 'Active' | 'Inactive' | 'Pending';
    avatar?: string;
    assignedProperties: string[];
    permissions: string[];
}

interface PropertyWithStaff extends Property {
    staffCount: number;
    staff: StaffMember[];
}

export const PropertyStaffManagement: React.FC = () => {
    const navigate = useNavigate();
    const { success, warning } = useToast();
    const { properties, fetchProperties, staff, fetchStaff } = useLandlordStore();
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProperty, setSelectedProperty] = useState<string>('all');
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Mock staff data - in real app, this would come from the store
    const mockStaff: StaffMember[] = [
        {
            id: '1',
            name: 'Alice Brown',
            email: 'alice@example.com',
            phone: '+254 712 345 678',
            role: 'Property Manager',
            status: 'Active',
            avatar: 'https://ui-avatars.com/api/?name=Alice+Brown&background=random',
            assignedProperties: ['prop_1', 'prop_2'],
            permissions: ['view_properties', 'manage_tenants', 'view_payments']
        },
        {
            id: '2',
            name: 'Bob White',
            email: 'bob@example.com',
            phone: '+254 722 123 456',
            role: 'Caretaker',
            status: 'Active',
            avatar: 'https://ui-avatars.com/api/?name=Bob+White&background=random',
            assignedProperties: ['prop_1'],
            permissions: ['view_properties', 'manage_maintenance']
        },
        {
            id: '3',
            name: 'Charlie Black',
            email: 'charlie@example.com',
            phone: '+254 733 789 012',
            role: 'Security Guard',
            status: 'Inactive',
            avatar: 'https://ui-avatars.com/api/?name=Charlie+Black&background=random',
            assignedProperties: ['prop_2'],
            permissions: ['view_properties']
        }
    ];

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await fetchProperties();
            // In real app: await fetchStaff();
            setLoading(false);
        };
        loadData();
    }, [fetchProperties]);

    const filteredStaff = mockStaff.filter(s => {
        const matchesSearch = s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            s.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            s.role.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesProperty = selectedProperty === 'all' || s.assignedProperties.includes(selectedProperty);
        return matchesSearch && matchesProperty;
    });

    const handleAssignStaff = async (staffId: string, propertyIds: string[]) => {
        setActionLoading(true);
        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 800));
            success('Success', 'Staff member assigned to properties successfully');
            setShowAssignModal(false);
        } catch (error) {
            warning('Error', 'Failed to assign staff to properties');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRemoveFromProperty = async (staffId: string, propertyId: string) => {
        setActionLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 500));
            success('Success', 'Staff member removed from property');
        } catch (error) {
            warning('Error', 'Failed to remove staff from property');
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusStyle = (status: string) => {
        switch (status) {
            case "Active":
                return { text: "text-green-600 dark:text-green-400", bg: "bg-green-100 dark:bg-green-900/30" };
            case "Inactive":
                return { text: "text-red-600 dark:text-red-400", bg: "bg-red-100 dark:bg-red-900/30" };
            case "Pending":
                return { text: "text-yellow-600 dark:text-yellow-400", bg: "bg-yellow-100 dark:bg-yellow-900/30" };
            default:
                return { text: "text-gray-600 dark:text-gray-400", bg: "bg-gray-100 dark:bg-gray-900/30" };
        }
    };

    const getPropertyStaffCount = (propertyId: string) => {
        return mockStaff.filter(s => s.assignedProperties.includes(propertyId)).length;
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <Loader2 className="animate-spin text-primary w-10 h-10" />
                <p className="mt-4 text-light-subtle dark:text-dark-subtle">Loading staff data...</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-12 min-h-[80vh]">
            {/* Header */}
            <div className="flex items-center gap-4 bg-light-card dark:bg-dark-card p-4 border-b border-light-border dark:border-dark-border">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                >
                    <ArrowLeft size={24} className="text-primary dark:text-golden" />
                </button>
                <div className="flex-1">
                    <h1 className="text-xl font-bold text-light-text dark:text-dark-text">Property Staff Management</h1>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle">Assign and manage staff for your properties</p>
                </div>
                <button
                    onClick={() => setShowAssignModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                    <Plus size={18} />
                    Assign Staff
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 px-4">
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-light-subtle dark:text-dark-subtle">Total Staff</p>
                            <p className="text-2xl font-bold text-light-text dark:text-dark-text mt-1">{mockStaff.length}</p>
                        </div>
                        <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                            <Users size={24} className="text-blue-600 dark:text-blue-400" />
                        </div>
                    </div>
                </div>
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-light-subtle dark:text-dark-subtle">Active Staff</p>
                            <p className="text-2xl font-bold text-light-text dark:text-dark-text mt-1">
                                {mockStaff.filter(s => s.status === 'Active').length}
                            </p>
                        </div>
                        <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                            <CheckCircle size={24} className="text-green-600 dark:text-green-400" />
                        </div>
                    </div>
                </div>
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-light-subtle dark:text-dark-subtle">Properties</p>
                            <p className="text-2xl font-bold text-light-text dark:text-dark-text mt-1">{properties.length}</p>
                        </div>
                        <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                            <Building size={24} className="text-purple-600 dark:text-purple-400" />
                        </div>
                    </div>
                </div>
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-light-subtle dark:text-dark-subtle">Pending Invites</p>
                            <p className="text-2xl font-bold text-light-text dark:text-dark-text mt-1">
                                {mockStaff.filter(s => s.status === 'Pending').length}
                            </p>
                        </div>
                        <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                            <Mail size={24} className="text-yellow-600 dark:text-yellow-400" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border px-4">
                <div className="flex flex-col md:flex-row gap-4">
                    {/* Search */}
                    <div className="relative flex-1">
                        <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-light-subtle dark:text-dark-subtle" />
                        <input
                            type="text"
                            placeholder="Search staff by name, email, or role..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-light-text dark:text-dark-text transition-all"
                        />
                    </div>
                    {/* Property Filter */}
                    <div className="md:w-64">
                        <select
                            value={selectedProperty}
                            onChange={(e) => setSelectedProperty(e.target.value)}
                            className="w-full px-4 py-2.5 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-light-text dark:text-dark-text transition-all"
                        >
                            <option value="all">All Properties</option>
                            {properties.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Staff List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 px-4">
                {filteredStaff.length === 0 ? (
                    <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
                        <Users size={48} className="text-light-subtle dark:text-dark-subtle mb-4 opacity-50" />
                        <p className="text-light-subtle dark:text-dark-subtle">No staff members found matching your criteria.</p>
                    </div>
                ) : (
                    filteredStaff.map(staffMember => (
                        <div
                            key={staffMember.id}
                            className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border hover:shadow-md transition-shadow"
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <img
                                        src={staffMember.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(staffMember.name)}&background=random`}
                                        alt={staffMember.name}
                                        className="w-12 h-12 rounded-full"
                                    />
                                    <div>
                                        <h3 className="font-bold text-light-text dark:text-dark-text">{staffMember.name}</h3>
                                        <p className="text-sm text-light-subtle dark:text-dark-subtle">{staffMember.role}</p>
                                    </div>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyle(staffMember.status).bg} ${getStatusStyle(staffMember.status).text}`}>
                                    {staffMember.status}
                                </span>
                            </div>

                            <div className="space-y-2 mb-4">
                                <div className="flex items-center gap-2 text-sm text-light-subtle dark:text-dark-subtle">
                                    <Mail size={14} />
                                    <span>{staffMember.email}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-light-subtle dark:text-dark-subtle">
                                    <Phone size={14} />
                                    <span>{staffMember.phone}</span>
                                </div>
                            </div>

                            {/* Assigned Properties */}
                            <div className="mb-4">
                                <p className="text-xs text-light-subtle dark:text-dark-subtle mb-2">Assigned Properties</p>
                                <div className="flex flex-wrap gap-1">
                                    {staffMember.assignedProperties.length === 0 ? (
                                        <span className="text-xs text-light-subtle dark:text-dark-subtle italic">No properties assigned</span>
                                    ) : (
                                        staffMember.assignedProperties.map(propId => {
                                            const prop = properties.find(p => p.id === propId);
                                            return prop ? (
                                                <span key={propId} className="px-2 py-1 bg-light-bg dark:bg-dark-bg rounded text-xs text-light-text dark:text-dark-text">
                                                    {prop.name}
                                                </span>
                                            ) : null;
                                        })
                                    )}
                                </div>
                            </div>

                            {/* Permissions Preview */}
                            <div className="flex items-center gap-2 mb-4">
                                <Shield size={14} className="text-light-subtle dark:text-dark-subtle" />
                                <span className="text-xs text-light-subtle dark:text-dark-subtle">
                                    {staffMember.permissions.length} permissions
                                </span>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2 pt-4 border-t border-light-border dark:border-dark-border">
                                <button
                                    onClick={() => navigate(`/landlord/staff/${staffMember.id}`)}
                                    className="flex-1 px-3 py-2 text-sm font-medium text-primary dark:text-golden bg-light-bg dark:bg-dark-bg rounded-lg hover:bg-light-bg/80 dark:hover:bg-dark-bg/80 transition-colors"
                                >
                                    View Details
                                </button>
                                <button
                                    onClick={() => {
                                        setSelectedStaff(staffMember);
                                        setShowAssignModal(true);
                                    }}
                                    className="flex-1 px-3 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
                                >
                                    Manage Properties
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Assign Staff Modal */}
            {showAssignModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 w-full max-w-md border border-light-border dark:border-dark-border">
                        <h2 className="text-xl font-bold text-light-text dark:text-dark-text mb-6">
                            {selectedStaff ? `Manage Properties - ${selectedStaff.name}` : 'Assign Staff to Properties'}
                        </h2>

                        <div className="space-y-4">
                            {!selectedStaff && (
                                <div>
                                    <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                        Select Staff Member
                                    </label>
                                    <select
                                        className="w-full px-4 py-2.5 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-light-text dark:text-dark-text"
                                    >
                                        <option value="">Choose a staff member...</option>
                                        {mockStaff.map(s => (
                                            <option key={s.id} value={s.id}>{s.name} - {s.role}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                    Select Properties
                                </label>
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {properties.map(property => {
                                        const isAssigned = selectedStaff?.assignedProperties.includes(property.id);
                                        return (
                                            <label key={property.id} className="flex items-center gap-3 p-3 bg-light-bg dark:bg-dark-bg rounded-lg cursor-pointer hover:bg-light-bg/80 dark:hover:bg-dark-bg/80 transition-colors">
                                                <input
                                                    type="checkbox"
                                                    defaultChecked={isAssigned}
                                                    className="w-4 h-4 text-primary rounded focus:ring-primary"
                                                />
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-light-text dark:text-dark-text">{property.name}</p>
                                                    <p className="text-xs text-light-subtle dark:text-dark-subtle">{property.address}</p>
                                                </div>
                                                {isAssigned && (
                                                    <span className="text-xs text-green-600 dark:text-green-400">Assigned</span>
                                                )}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowAssignModal(false);
                                    setSelectedStaff(null);
                                }}
                                className="flex-1 px-4 py-2.5 border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleAssignStaff(selectedStaff?.id || '', [])}
                                disabled={actionLoading}
                                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {actionLoading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};